/**
 * Utility functions for managing patient data in localStorage
 * Dispatches custom events so components can react to changes in the same tab
 */

import { logAudit } from "./auditLog";
import { getCurrentUserEmail, getKnownUsers } from "./auth";

const readPatientsRaw = () => {
  try {
    const stored = localStorage.getItem("patients");
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Keys whose changes should be logged as patient updates. We avoid logging
// every micro-change (e.g. nested timestamps) but do want the clinically
// meaningful records — status, onboarding, consent, medical history,
// eligibility criteria, treatment, follow-up, discharge, and attribution —
// to be visible in the audit trail with their before/after values. Consent,
// medicalHistory, and criteria are exactly what an FDA/CVM reviewer expects
// to see version-tracked, so they must be here.
const TRACKED_TOP_LEVEL_KEYS = [
  "status",
  "onboardingStatus",
  "treatmentStatus",
  "followUpStatus",
  "survivalDischargeStatus",
  "consent",
  "medicalHistory",
  "criteria",
  "onboardingData",
  "treatmentSchedule",
  "treatmentProgress",
  "followUpSchedule",
  "followUpProgress",
  "survivalDischarge",
  "assignedVetEmail",
  "name",
  "trialId",
];

// Compare a patient's prev/next snapshots across the tracked keys and return a
// list of `{ key, from, to }` records. Full before/after values are captured
// (not just the key name) so the audit trail can reconstruct exactly what
// changed — a 21 CFR Part 11 §11.10(e) expectation.
const diffChanges = (prev, next) => {
  const changes = [];
  TRACKED_TOP_LEVEL_KEYS.forEach((key) => {
    const a = prev ? prev[key] : undefined;
    const b = next ? next[key] : undefined;
    if (JSON.stringify(a) !== JSON.stringify(b)) {
      changes.push({
        key,
        from: a === undefined ? null : a,
        to: b === undefined ? null : b,
      });
    }
  });
  return changes;
};

const logPatientDiffs = (prevList, nextList) => {
  const prevById = new Map(prevList.map((p) => [p.id, p]));
  const nextById = new Map(nextList.map((p) => [p.id, p]));

  nextById.forEach((patient, id) => {
    const prev = prevById.get(id);
    if (!prev) {
      logAudit({
        action: "patient.created",
        entityType: "patient",
        entityId: id,
        entityLabel: patient.name || null,
        details: { status: patient.status || null, trialId: patient.trialId || null },
        source: "patientStorage",
      });
      return;
    }
    const changes = diffChanges(prev, patient);
    if (changes.length === 0) return;

    const changed = changes.map((c) => c.key);
    const isStatusChange = changed.includes("status");
    const action = isStatusChange
      ? `patient.status.${patient.status || "unknown"}`
      : "patient.updated";
    logAudit({
      action,
      entityType: "patient",
      entityId: id,
      entityLabel: patient.name || null,
      details: {
        // Names of the fields that changed (quick scan) plus the full
        // before/after values for each so history is reconstructable.
        changed,
        changes,
        previousStatus: prev.status || null,
        status: patient.status || null,
      },
      source: "patientStorage",
    });
  });

  prevById.forEach((patient, id) => {
    if (!nextById.has(id)) {
      logAudit({
        action: "patient.deleted",
        entityType: "patient",
        entityId: id,
        entityLabel: patient.name || null,
        source: "patientStorage",
      });
    }
  });
};

export const savePatients = (patients) => {
  const prev = readPatientsRaw();
  const inputList = Array.isArray(patients) ? patients : [];

  // Backfill: any patient missing `assignedVetEmail` inherits the current
  // user so the audit log can attribute it to the vet that saved it. Existing
  // assignments are never overwritten here.
  const currentVet = getCurrentUserEmail();
  const next = inputList.map((p) =>
    p && !p.assignedVetEmail && currentVet
      ? { ...p, assignedVetEmail: currentVet }
      : p
  );

  localStorage.setItem("patients", JSON.stringify(next));

  try {
    logPatientDiffs(prev, next);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("Failed to log patient audit diff", err);
  }

  // Dispatch custom event so components in the same tab can react
  window.dispatchEvent(new CustomEvent("patientsUpdated", {
    detail: { patients: next }
  }));
  
  // Also dispatch storage event for cross-tab compatibility
  window.dispatchEvent(new StorageEvent("storage", {
    key: "patients",
    newValue: JSON.stringify(next),
    storageArea: localStorage
  }));
};

export const getPatients = () => {
  const stored = localStorage.getItem("patients");
  return stored ? JSON.parse(stored) : [];
};

/**
 * One-time self-heal: for any patient in localStorage missing
 * `assignedVetEmail`, try to recover the creator from the audit log
 * (`patient.created` event's actor), falling back to any actor who has ever
 * touched the patient, then to the current user if they're the only known
 * user in the system.
 *
 * This is idempotent and cheap to run on every app load / audit page refresh.
 * It writes silently — no new audit entries — because it's just recovering
 * data that was always implied, not making a user-facing change.
 *
 * Returns `true` if any patient record was updated.
 */
export const backfillAssignedVetEmails = () => {
  const list = readPatientsRaw();
  if (list.length === 0) return false;

  const missing = list.filter((p) => p && p.id && !p.assignedVetEmail);
  if (missing.length === 0) return false;

  // Read audit log directly to avoid a circular dependency on a filtered getter.
  let auditEntries = [];
  try {
    const raw = localStorage.getItem("auditLog");
    auditEntries = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(auditEntries)) auditEntries = [];
  } catch {
    auditEntries = [];
  }

  const creatorByPatient = new Map();
  const anyActorByPatient = new Map();
  auditEntries.forEach((ev) => {
    if (!ev || ev.entityType !== "patient" || !ev.entityId) return;
    const actor =
      ev.actorEmail && ev.actorEmail !== "unknown"
        ? String(ev.actorEmail).trim().toLowerCase()
        : null;
    if (!actor) return;
    if (ev.action === "patient.created" && !creatorByPatient.has(ev.entityId)) {
      creatorByPatient.set(ev.entityId, actor);
    }
    if (!anyActorByPatient.has(ev.entityId)) {
      anyActorByPatient.set(ev.entityId, actor);
    }
  });

  const knownUsers = (getKnownUsers() || []).filter(Boolean);
  const currentUser = getCurrentUserEmail();
  const soleUser =
    knownUsers.length === 1
      ? knownUsers[0]
      : knownUsers.length === 0 && currentUser
      ? currentUser
      : null;

  let changed = false;
  const next = list.map((p) => {
    if (!p || !p.id || p.assignedVetEmail) return p;
    const recovered =
      creatorByPatient.get(p.id) ||
      anyActorByPatient.get(p.id) ||
      soleUser ||
      null;
    if (!recovered) return p;
    changed = true;
    return { ...p, assignedVetEmail: recovered };
  });

  if (!changed) return false;

  localStorage.setItem("patients", JSON.stringify(next));
  window.dispatchEvent(
    new CustomEvent("patientsUpdated", { detail: { patients: next } })
  );
  return true;
};

/**
 * Attribute a patient to a specific veterinarian. Used by the admin audit
 * page to claim "unassigned" patients. Writes an explicit audit entry so the
 * reassignment is traceable.
 */
export const reassignPatient = (patientId, vetEmail) => {
  const normalized =
    typeof vetEmail === "string" ? vetEmail.trim().toLowerCase() : "";
  if (!patientId || !normalized) return false;

  const list = readPatientsRaw();
  const idx = list.findIndex((p) => p && p.id === patientId);
  if (idx === -1) return false;

  const previous = list[idx].assignedVetEmail || null;
  if (previous === normalized) return false;

  const next = list.slice();
  next[idx] = { ...list[idx], assignedVetEmail: normalized };
  localStorage.setItem("patients", JSON.stringify(next));

  logAudit({
    action: "patient.reassigned",
    entityType: "patient",
    entityId: patientId,
    entityLabel: next[idx].name || null,
    details: { previousVet: previous, newVet: normalized },
    source: "patientStorage.reassignPatient",
  });

  window.dispatchEvent(
    new CustomEvent("patientsUpdated", { detail: { patients: next } })
  );
  window.dispatchEvent(
    new StorageEvent("storage", {
      key: "patients",
      newValue: JSON.stringify(next),
      storageArea: localStorage,
    })
  );

  return true;
};
